/* eslint-disable jsx-a11y/media-has-caption */
import React, { useRef, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import IconButton from '@mui/material/IconButton';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Badge from '@mui/material/Badge';
import agent from '../../lib/agent';

export default function FeedbackForm({ open, onClose }) {
  const videoRef = useRef();
  const textFieldRef = useRef(null);

  const [hidden, setHidden] = useState(null);
  const [stream, setStream] = useState(null);
  const [files, setFiles] = useState([]);
  const [videoBlobs, setVideoBlobs] = useState([]);

  const onDrop = acceptedFiles => {
    setFiles(acceptedFiles);
  };

  const { getRootProps, getInputProps, open: openFileDialog } = useDropzone({
    onDrop,
    noClick: true
  });

  const [submitted, setSubmitted] = useState(false);
  const title = submitted ? '' : 'Feedback';
  const stopRecording = async () => {
    setHidden(false);

    // recorder.stop();
    // stream.getVideoTracks()[0].stop();
    setStream(null);
  };
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { mediaSource: 'screen' }
    });
    setStream(stream);
    setHidden(true);

    const recorder = new MediaRecorder(stream);
    stream.getVideoTracks()[0].onended = () => {
      stopRecording();
    };

    const chunks = [];
    recorder.ondataavailable = e => chunks.push(e.data);
    recorder.onstop = () => {
      const completeBlob = new Blob(chunks, { type: chunks[0].type });
      setVideoBlobs([completeBlob, ...videoBlobs]);
    };

    recorder.start();
  };

  useEffect(() => {
    if (videoBlobs.length) {
      const blob = URL.createObjectURL(videoBlobs[0]);
      videoRef.current.src = blob;
    }
  }, [videoBlobs, videoBlobs]);

  const handleSubmit = async () => {
    const formData = new FormData();

    files.forEach(file => formData.append('file[]', file));
    videoBlobs.forEach((blob, i) =>
      formData.append('file[]', blob, `blob${i}.mp4`)
    );

    const { userAgent } = navigator;
    const { href } = window.location;

    formData.append('user_agent', userAgent);
    formData.append('href', href);

    const { value } = textFieldRef.current;
    formData.append('feedback', value);
    await agent.User.sendFeedback(formData);
    setSubmitted(true);
    setTimeout(() => {
      onClose();
    }, 3000);
  };

  const style = hidden
    ? {
        display: 'none',
        pointerEvents: 'none'
      }
    : {};

  return (
    <Dialog style={style} open={open} onClose={onClose} {...getRootProps()}>
      <input
        webkitdirectory
        directory
        multiple
        {...getInputProps({ disabled: false })}
      />
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <DialogContent>
        {!submitted && (
          <>
            <DialogContentText>
              Please use this form for suggesting improvements and for
              submitting issues
            </DialogContentText>
            <TextField
              inputRef={textFieldRef}
              multiline
              rows={4}
              autoFocus
              margin="dense"
              id="feedbcack"
              label="Feedback"
              variant="outlined"
              type="text"
              fullWidth
            />

            {videoBlobs.length > 0 && (
              <Box>
                <video controls width="100%" ref={videoRef} autoPlay />
              </Box>
            )}

            <Box display="flex" justifyContent="space-between">
              <Box alignItems="center">
                <Button
                  variant="contained"
                  style={{
                    border: 'none',
                    boxShadow: 'none',
                    textTransform: 'initial',
                    background: 'none'
                  }}
                  onClick={() => {
                    if (!stream) startRecording();
                    if (stream) stopRecording();
                  }}
                  startIcon={
                    stream ? (
                      <FiberManualRecordIcon />
                    ) : (
                      <FiberManualRecordIcon color="secondary" />
                    )
                  }
                >
                  {!videoBlobs.length
                    ? `Press to record a video`
                    : `Press to record another video`}
                </Button>
                <IconButton
                  color="primary"
                  aria-label="upload picture"
                  component="span"
                  style={{
                    color: 'red'
                  }}
                  size="large"
                />
              </Box>
              <IconButton
                aria-label="Close"
                onClick={openFileDialog}
                size="medium"
              >
                <Badge badgeContent={files.length} color="secondary">
                  <AttachFileIcon />
                </Badge>
              </IconButton>
            </Box>
          </>
        )}

        {submitted && (
          <>
            <Box p={4} style={{ textAlign: 'center' }}>
              <BugReportOutlinedIcon
                fontSize="large"
                className="feedback-bug"
                color="secondary"
              />

              <Typography gutterBottom variant="h5">
                Thank you for your time !
              </Typography>
              <Typography variant="body1">
                We will let you know when this issue has been solved
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      {!submitted && (
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleSubmit();
            }}
            color="primary"
          >
            Send
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
